import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Disclaimer from "../../components/Disclaimer/Disclaimer";
import FestCard from "../../components/FestCard/FestCard";
import FestMap from "../../components/FestMap/FestMap";
import Layout from "../../components/Layout/Layout";
import { FESTS, KINDS, REGIONS } from "../../data/fests";
import { KIND_LABELS, matchesFilters, TAG_LABELS } from "../../lib/access";
import { formatCoords, nearestFests } from "../../lib/geo";
import { reversePlace, searchPlaces } from "../../lib/geocode";
import { formatRange, nextSeason } from "../../lib/season";
import type { Fest, FestKind, GeocodeHit, MapInspect } from "../../types";

const MapView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<GeocodeHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [region, setRegion] = useState<Fest["region"] | "">("");
  const [kind, setKind] = useState<FestKind | "">("");
  const [camp, setCamp] = useState(false);
  const [rvFriendly, setRvFriendly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(
    searchParams.get("fest"),
  );
  const [inspect, setInspect] = useState<MapInspect | null>(null);
  const initialFest = FESTS.find((entry) => entry.id === searchParams.get("fest"));
  const [flyTo, setFlyTo] = useState<{
    latitude: number;
    longitude: number;
    zoom?: number;
  } | null>(
    initialFest
      ? {
          latitude: initialFest.latitude,
          longitude: initialFest.longitude,
          zoom: 8,
        }
      : null,
  );
  const [panelOpen, setPanelOpen] = useState(false);

  const visibleFests = useMemo(() => {
    const filtered = FESTS.filter((fest) =>
      matchesFilters(fest, { region, kind, camp, rvFriendly }),
    );
    if (selectedId && !filtered.some((fest) => fest.id === selectedId)) {
      const extra = FESTS.find((fest) => fest.id === selectedId);
      if (extra) return [...filtered, extra];
    }
    return filtered;
  }, [region, kind, camp, rvFriendly, selectedId]);

  const selected = FESTS.find((fest) => fest.id === selectedId) ?? null;
  const selectedSeason = selected ? nextSeason(selected) : null;

  useEffect(() => {
    if (selectedId) setPanelOpen(true);
  }, [selectedId]);

  useEffect(() => {
    if (selectedId) {
      setSearchParams({ fest: selectedId }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [selectedId, setSearchParams]);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setSearching(true);
      searchPlaces(query)
        .then((results) => {
          if (!cancelled) setHits(results);
        })
        .catch(() => {
          if (!cancelled) setHits([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [q]);

  async function inspectPoint(lat: number, lng: number, fly = false) {
    setSelectedId(null);
    setInspect({
      latitude: lat,
      longitude: lng,
      placeName: null,
      nearby: nearestFests(FESTS, lat, lng, 3),
    });
    setPanelOpen(true);
    if (fly) {
      setFlyTo({ latitude: lat, longitude: lng, zoom: 8 });
    }
    const place = await reversePlace(lat, lng).catch(() => null);
    setInspect((current) =>
      current && current.latitude === lat && current.longitude === lng
        ? { ...current, placeName: place }
        : current,
    );
  }

  function selectFest(fest: Fest) {
    setInspect(null);
    setSelectedId(fest.id);
    setFlyTo({ latitude: fest.latitude, longitude: fest.longitude, zoom: 8 });
    setPanelOpen(true);
  }

  function useMyLocation() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => inspectPoint(pos.coords.latitude, pos.coords.longitude, true),
      () => undefined,
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  return (
    <Layout fullHeight>
      <div className="relative flex-1 min-h-0 h-full">
        <div className="absolute inset-0 bg-alt-blk">
          <FestMap
            fests={visibleFests}
            selectedId={selectedId}
            onSelectFest={selectFest}
            onInspect={(lat, lng) => inspectPoint(lat, lng, false)}
            flyTo={flyTo}
            inspect={
              inspect
                ? { latitude: inspect.latitude, longitude: inspect.longitude }
                : null
            }
          />
        </div>

        <aside className="absolute z-[1100] top-4 left-4 right-4 md:right-auto md:w-[400px] max-h-[min(52vh,calc(100%-2rem))] md:max-h-[calc(100%-2rem)] overflow-y-auto rounded-2xl bg-alt-blk text-alt-wht shadow-2xl p-4 md:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl text-ray font-bold">Forteana</h1>
              <p className="text-xs md:text-sm text-alt-wht/80 mt-1">
                {FESTS.length} places · overnight the weird
              </p>
            </div>
            <button
              type="button"
              className="md:hidden shrink-0 text-sm px-3 py-1.5 rounded-xl border border-alt-wht/30 hover:border-ray"
              onClick={() => setPanelOpen((open) => !open)}
              aria-expanded={panelOpen}
            >
              {panelOpen ? "Map" : "Panel"}
            </button>
          </div>
          <div className="mt-2">
            <Disclaimer compact dark />
          </div>

          <div className={`space-y-4 mt-4 ${panelOpen ? "block" : "hidden"} md:block`}>
            <div>
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search a place — Point Pleasant, Marfa, Roswell…"
                className="w-full rounded-xl bg-white/10 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-ray"
              />
              {searching && (
                <p className="text-xs text-alt-wht/50 mt-1">Searching…</p>
              )}
              {hits.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {hits.map((hit) => (
                    <li key={`${hit.latitude}-${hit.longitude}-${hit.name}`}>
                      <button
                        type="button"
                        className="w-full text-left text-sm rounded-lg px-2 py-1.5 hover:bg-white/10"
                        onClick={() => {
                          setQ("");
                          setHits([]);
                          void inspectPoint(hit.latitude, hit.longitude, true);
                        }}
                      >
                        {hit.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={useMyLocation}
                className="mt-2 text-xs text-ray hover:text-sunset"
              >
                Use my location
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {(["", ...REGIONS] as const).map((value) => (
                <button
                  key={value || "all"}
                  className={`text-xs px-3 py-1 rounded-full border ${
                    region === value
                      ? "bg-mars border-mars text-white"
                      : "border-white/20 hover:border-ray"
                  }`}
                  onClick={() => setRegion(value)}
                >
                  {value || "All regions"}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {(["", ...KINDS] as const).map((value) => (
                <button
                  key={value || "all-kinds"}
                  className={`text-xs px-3 py-1 rounded-full border ${
                    kind === value
                      ? "bg-mars border-mars text-white"
                      : "border-white/20 hover:border-ray"
                  }`}
                  onClick={() => setKind(value)}
                >
                  {value ? KIND_LABELS[value] : "All kinds"}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <FilterChip
                label="Overnight trip"
                active={camp}
                onClick={() => setCamp((v) => !v)}
              />
              <FilterChip
                label="RV-friendly"
                active={rvFriendly}
                onClick={() => setRvFriendly((v) => !v)}
              />
            </div>

            <p className="text-xs text-alt-wht/70">
              <Link to="/calendar" className="text-ray hover:text-sunset">
                Upcoming seasons
              </Link>
            </p>

            {inspect && (
              <div className="border-t border-white/10 pt-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-ray">This spot</h2>
                    <p className="text-xs text-alt-wht/70">
                      {inspect.placeName ?? "Map click"} ·{" "}
                      {formatCoords(inspect.latitude, inspect.longitude)}
                    </p>
                  </div>
                  <button
                    className="text-xs text-sunset hover:text-ray"
                    onClick={() => setInspect(null)}
                  >
                    Clear
                  </button>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-alt-wht/50 mb-2">
                    Nearby places
                  </p>
                  <div className="space-y-2">
                    {inspect.nearby.map((fest) => (
                      <FestCard
                        key={fest.id}
                        fest={fest}
                        miles={fest.miles}
                        compact
                        onSelect={selectFest}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {selected && (
              <div className="border-t border-white/10 pt-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-ray">{selected.name}</h2>
                    <p className="text-xs text-alt-wht/70">
                      {selected.city}, {selected.state}
                      {selectedSeason
                        ? ` · ${formatRange(selectedSeason.start, selectedSeason.end)}`
                        : ""}
                    </p>
                  </div>
                  <button
                    className="text-xs text-sunset hover:text-ray"
                    onClick={() => setSelectedId(null)}
                  >
                    Clear
                  </button>
                </div>
                <p className="text-sm text-alt-wht/85">{selected.whyGo}</p>
                <div className="flex flex-wrap gap-1.5">
                  {selected.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-white/15"
                    >
                      {TAG_LABELS[tag]}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-alt-wht/70">{selected.campingNotes}</p>
                <div className="flex flex-wrap gap-3 items-center">
                  <Link
                    to={`/fests/${selected.id}`}
                    className="inline-block text-sm text-ray hover:text-sunset"
                  >
                    Trip notes →
                  </Link>
                  {selected.officialUrl && (
                    <a
                      href={selected.officialUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block text-sm text-ray hover:text-sunset"
                    >
                      Official page
                    </a>
                  )}
                </div>
              </div>
            )}

            {!selected && !inspect && (
              <div>
                <p className="text-xs text-alt-wht/60 mb-2">
                  {visibleFests.length} fests · tap a pin or the map
                </p>
                <ul className="space-y-2 max-h-48 md:max-h-64 overflow-y-auto">
                  {visibleFests.map((fest) => (
                    <li key={fest.id}>
                      <FestCard fest={fest} compact onSelect={selectFest} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-[11px] text-alt-wht/45 leading-relaxed">
              Built for comfortable multi-night stays next to the weird.{" "}
              <a
                href="https://earthboundmartian.com"
                className="text-ray/80 hover:text-sunset"
              >
                Landships at Earthbound Martian
              </a>
              .
            </p>
          </div>
        </aside>

        <div className="hidden md:block absolute z-[1100] bottom-4 right-4 rounded-2xl bg-alt-blk/90 text-alt-wht p-3 text-xs shadow-xl">
          <div className="font-semibold text-ray mb-1.5">Pins by kind</div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-alt-blk border border-sunset" />
            Ghosts / haunts
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-turquoise" />
            Cryptids / Bigfoot
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-sun" />
            UFO / UAP
          </div>
        </div>
      </div>
    </Layout>
  );
};

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`text-xs px-3 py-1 rounded-full border ${
        active ? "bg-mars border-mars text-white" : "border-white/20 hover:border-ray"
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

export default MapView;
